<template>
    <loading v-if="fetchingData"/>
    <div v-else-if="form" class="space-y-16">
        <div>
            <h2 class="font-semibold mt-16 mb-8 text-body">{{ $t('billing_details.company') }}</h2>
            <div class="grid grid-cols-4 gap-y-8 gap-x-16">
                <simple-input
                    class="col-span-full"
                    :errors="form.errors.name"
                    v-model="form.name"
                >
                    {{ $t('billing_details.name') }}
                </simple-input>
                <simple-input
                    class="col-span-2"
                    placeholder="john.doe@example.com"
                    v-model="form.email" :errors="form.errors.email"
                >
                    {{ $t('billing_details.email') }}
                </simple-input>
                <simple-input
                    class="col-span-2"
                    :errors="form.errors.phone"
                    placeholder="0412345678"
                    v-model="form.phone"
                >
                    {{ $t('billing_details.mobile') }}
                </simple-input>
                <simple-input
                    class="col-span-2"
                    :errors="form.errors.vat"
                    placeholder="BE0123456789"
                    v-model="form.vat"
                >
                    {{ $t('billing_details.vat', { vat_label: taxNumberLabelForRegion }) }}
                </simple-input>
                <select-dropdown
                    class="col-span-2"
                    :options="availableLanguages"
                    v-if="australianLabels"
                    v-model="form.lang"
                >
                    {{ $t('billing_details.lang') }}
                </select-dropdown>
                <simple-input
                    :class="australianLabels ? 'col-span-full': 'col-span-2'"
                    :errors="form.errors.invoice_ref"
                    v-model="form.invoice_ref"
                >
                    {{ $t('billing_details.invoice_ref') }} ({{ $t('general.optional') }})
                </simple-input>
            </div>
        </div>
        <div>
            <h2 class="font-semibold mb-8 text-body">{{ $t('billing_details.address') }}</h2>
            <div class="grid grid-cols-4 gap-y-8 gap-x-16">
                <simple-input
                    v-model="form.street"
                    :errors="form.errors.street"
                    class="col-span-3"
                >
                    {{ $t('billing_details.street') }}
                </simple-input>
                <simple-input v-model="form.number" :errors="form.errors.number">
                    {{ $t('billing_details.number') }}
                </simple-input>
                <simple-input v-model="form.postal_code" :errors="form.errors.postal_code">
                    {{ $t('billing_details.postal_code') }}
                </simple-input>
                <simple-input
                    v-model="form.city"
                    :errors="form.errors.city"
                    class="col-span-3"
                >
                    {{ $t('billing_details.city') }}
                </simple-input>
                <simple-input
                    v-model="form.state"
                    :errors="form.errors.state"
                    class="col-span-2"
                >
                    {{ $t('billing_details.state') }} ({{ $t('general.optional') }})
                </simple-input>
                <select-dropdown
                    :autoTranslate="false"
                    class="col-span-2"
                    :options="countries"
                    :value="form.country"
                    searchable
                    @change="form.country = $event">
                    {{ $t('billing_details.country') }}
                </select-dropdown>
            </div>
        </div>

        <banner
            v-if="billingDetails && !billingDetails.mandate_signed && billingDetails.mandate_key"
            :title="$t('settings.billing_details.info_debit.title')"
            :description="$t('settings.billing_details.info_debit.description')"
            :link="billingDetails.mandate_key"
            :link-text="$t('settings.billing_details.info_debit.confirmText')"
            info
            inline
            :one-line="false"
        />

        <banner
            v-if="billingDetails && billingDetails.mandate_signed"
            :title="$t('settings.billing_details.success_debit.title')"
            :description="$t('settings.billing_details.success_debit.description')"
            success
            inline
            :one-line="false"
        >
            <template #buttons>
                    <a :href="billingDetails.pdf">
                        {{ $t('settings.billing_details.success_debit.confirmText') }}
                    </a>
                    <a href="javascript:" @click="startCancellingMandate">
                        {{ $t('settings.billing_details.success_debit.cancel_mandate') }}
                    </a>
            </template>
        </banner>

        <simple-button
            :loading="performingUpdate"
            class="ml-auto mt-32"
            display="secondary"
            icon="save"
            look="primary"
            @click="update"
        >
            {{ $t('general.save') }}
        </simple-button>

        <popup-modal-confirm v-if="cancellingMandate"
                             :title="$t('settings.billing_details.cancel_mandate.title')"
                             :message="$t('settings.billing_details.cancel_mandate.message')"
                             @close="stopCancellingMandate" @confirm="cancelMandate" :working="performingCancelMandateRequest"/>
    </div>
</template>

<script>
import Input from '../forms/Input';
import SimpleButton from '../ui/SimpleButton';
import SelectDropdown from '../forms/SelectDropdown';
import * as billingDetailsService from '../../services/BillingDetailsService';
import BillingDetails from '../../models/BillingDetails';
import {normalizeJson} from '../../../shared/helpers';
import Form from '../../etc/forms/Form';
import Loading from '../ui/Loading';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useCurrencyStore} from '../../state/modules/currency';
import PopupModalConfirm from "../ui/PopupModalConfirm";
import RegionLabel from "../../mixins/regionLabel";
import Banner from "../ui/Banner.vue";

export default {
    components: {
        Banner,
        PopupModalConfirm,
        Loading,
        SelectDropdown,
        SimpleButton,
        'simple-input': Input,
    },
    data() {
        return {
            fetchingData: false,
            form: null,
            performingUpdate: false,

            cancellingMandate: false,
            performingCancelMandateRequest: false
        };
    },
    mixins: [
        RegionLabel,
    ],
    computed: {
        ...mapState(useMainStore, ['locale', 'availableLanguages', 'currentCompany']),
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        countries() {
            return Object.keys(this.$tm('countries')).map((key) => ({
                label: this.$rt(this.$tm('countries')[key]),
                value: key,
            })).concat([{
                label: '--- Select country ---',
                value: null,
            }]);
        },
        billingDetails() {
            return useRepo(BillingDetails).where('company_id', this.currentCompanyId).first();
        },
        countryCode(){
            if(this.currentCompany.country){
                return this.currentCompany.country;
            }

            return '';
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useCurrencyStore, { clearCurrency: 'clear' }),
        fetch() {
            this.fetchingData = true;

            billingDetailsService.show(this.currentCompanyId).then(result => {
                if (result) {
                    return useRepo(BillingDetails).save(normalizeJson(result));
                }
            }).then(() => {
                this.form = this.generateForm();
                this.fetchingData = false;
            });
        },
        generateForm() {
            let fields = [
                {name: 'name', value: ''},
                {name: 'email', value: ''},
                {name: 'phone', value: ''},
                {name: 'street', value: ''},
                {name: 'number', value: ''},
                {name: 'postal_code', value: ''},
                {name: 'city', value: ''},
                {name: 'state', value: ''},
                {name: 'country', value: this.countryCode},
                {name: 'vat', value: ''},
                {name: 'lang', value: this.locale},
                {name: 'invoice_ref', value: ''},
            ];

            if (this.billingDetails) {
                fields.forEach((field, index) => {
                    if (typeof this.billingDetails[field.name] !== 'undefined') {
                        fields[index].value = JSON.parse(JSON.stringify(this.billingDetails[field.name]));
                    }
                });
            }

            return new Form(fields);
        },

        update() {
            this.performingUpdate = true;

            this.form.submit(data => {
                return billingDetailsService.update(this.currentCompanyId, {
                    data: {
                        attributes: data,
                    },
                });
            }).then(result => {
                return useRepo(BillingDetails).save(normalizeJson(result));
            }).then(() => {
                this.form = this.generateForm();

                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('billing_details.saved'),
                });
            }).finally(() => {
                this.clearCurrency();
                this.performingUpdate = false;
            });
        },

        startCancellingMandate() {
            this.cancellingMandate = true;
        },
        stopCancellingMandate() {
            this.cancellingMandate = false;
        },
        cancelMandate() {
            this.performingCancelMandateRequest = true;

            billingDetailsService.cancelMandate(this.currentCompanyId)
                .then(result => {
                    return useRepo(BillingDetails).save(normalizeJson(result));
                })
                .finally(() => {
                    this.performingCancelMandateRequest = false;
                    this.stopCancellingMandate();
                });
        }
    },
    mounted() {
        this.fetch();
    },
};
</script>
